import * as d3 from 'd3';
import { appendAiUnreadDot } from './appendAiUnreadDot';
import { FORM_CHECK_COLOR, FORM_CHECK_INK, resolveFormCheckState } from '../../utils/formCheckDisplay';
import { NODE_CARD_WIDTH, nodeCardHeight, nodeHasScores } from './nodeCardMetrics';
import { isLowGfx } from '../../utils/lowGfx';
import { valenceStyle } from '../../config/valence';
import { SCENARIO_COLORS_BY_NAME } from '../../config/brandColors';
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { getScenarioLucideIcon } from './scenarioIcons';

function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string') return null;
  const raw = hex.replace('#', '').trim();
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveScenarioNodeColor({ scenarioColor, color, title }) {
  if (scenarioColor) return scenarioColor;
  if (color) return color;
  if (!title) return null;
  const base = String(title).split(':')[0].trim();
  return SCENARIO_COLORS_BY_NAME[title]
    || SCENARIO_COLORS_BY_NAME[base]
    || SCENARIO_COLORS_BY_NAME[base.toLowerCase()]
    || null;
}

/**
 * renderImpactPathNode - MINIMAL CLEAN STYLE (NEW DEFAULT)
 *
 * Simple, focused, distraction-free node rendering
 * - 1px gray borders
 * - Flat colors (no gradients)
 * - Clean typography
 * - Minimal visual noise
 *
 * @param {d3.Selection} selection - D3 selection to render into (svg or g element)
 * @param {Object} data - Node data
 * @param {string} data.title - Node title text
 * @param {string} data.mood - Mood (for compatibility, not used in minimal style)
 * @param {string|string[]} data.categories - Categories (for compatibility)
 * @param {number} data.depth - Node depth in the tree (0 = root)
 * @param {boolean} data.isHuman - Whether this is a human-contributed node
 * @param {string} data.type - Node type ('start', 'ai-generated', 'human', etc.)
 * @param {number} data.reactions - Net reaction count
 * @param {Function} data.onClick - Optional click handler
 * @param {Array} data.members - Array of merged node IDs (for grouped nodes)
 * @param {string} data.participantName - Participant name for start nodes
 * @param {string} data.created_by - Creator identifier ('system' or participant ID)
 */
export function renderImpactPathNode(selection, data) {
  const {
    title = 'Node Title',
    mood = 'neutral',
    categories = [],
    category = null,
    depth = 0,
    isHuman = false,
    type = 'human',
    reactions = 0,
    // Valence layer (§4b). `valence` null means nobody has judged this node —
    // distinct from 'unknown', which is a judgement. Unjudged nodes draw no
    // rail at all: the map should not claim an opinion the room never gave.
    valence = null,
    isChoicePoint = false,
    onClick = null,
    members = [],
    participantName = null,
    created_by = null,
    is_scenario_starter = false, // Muuttuja marker — drives the type label
    isOwnNode = false, // Whether this node belongs to the current user
    // Impact Path v2: colour of the edge that reaches this node, when someone
    // chose a type for it. The relation and the consequence it lands on then
    // read as one statement instead of a coloured line into a neutral box.
    borderColor = null,
    // Scenario accent for depth-0 root cards (prop, node.color, or name lookup)
    scenarioColor = null,
    color = null,
    isProcessing = false, // AI is working on this node (show glimmer)
    hasUnreadAi = false, // Completed AI evaluation not yet opened
    zoomDotScale = 1, // Counter-scale for canvas zoom (keeps dot visible when zoomed out)
    isPulsing = false, // Active editing / add-consequence context (subtle ring)
    hasSuggestion = false, // An undecided AI proposal touches this node
    isFreshHighlight = false, // Newly created node (brief glow)
    node_analysis = null, // { research_support: -100..100, quality: 0..100 }
    // AI-enhanced content separation (Dec 2025)
    human_text = null,
    ai_analysis = null,
    ai_sources = [],
    // Expanded state (shows description)
    isExpanded = false,
    description = null,
    nodeTypeLabels = null,
  } = data;

  const labels = nodeTypeLabels || {
    scenario: 'Skenaario',
    variable: 'Muuttuja',
    consequence: 'Seuraus',
    start: 'START',
  };

  // Faulty input indicator (node_analysis.formCheck in warn state).
  // Only render for "own" nodes to match the modal behavior.
  // Starters (muuttujat) never show form-check warnings — vague factors by design.
  const formCheck = is_scenario_starter ? null : (node_analysis?.formCheck || null);
  const formCheckState = isOwnNode ? resolveFormCheckState({ formCheck }) : 'none';
  const isFaultyInput = formCheckState === 'warn';
  const faultyIssues = Array.isArray(formCheck?.issues) ? formCheck.issues : [];
  const faultyTooltipText = faultyIssues
    .map((i) => (i && typeof i === 'object' ? (i.message || i.code) : ''))
    .filter(Boolean)
    .join('\n');

  const cardWidth = NODE_CARD_WIDTH;
  // Check if we have description to show
  const hasDescription = description && description !== 'None' && description !== 'null' && description.trim();

  // Card height: normal 90px, +20px when scores shown, expanded 400px. Shared
  // with anything that has to sit on the card — see nodeCardMetrics.js.
  const hasScores = nodeHasScores({ is_scenario_starter, node_analysis });
  const cardHeight = nodeCardHeight({ is_scenario_starter, node_analysis }, { isExpanded, description });

  // Clear any existing content
  selection.selectAll('*').remove();

  // Check if this is a grouped node (has members)
  const isGrouped = members && members.length > 0;
  const isStartNode = type === 'start' || mood === 'start';
  const isScenarioNode = depth === 0 && !isStartNode;
  const resolvedScenarioColor = isScenarioNode
    ? resolveScenarioNodeColor({ scenarioColor, color, title })
    : null;
  // Valence is a property of the node, not a mode: once judged, a node IS
  // green or red — tint and border — with no layer to switch on. Unjudged
  // stays the plain card. Scenario nodes keep their scenario tint.
  const valenceTint = valence && !isScenarioNode ? valenceStyle(valence).tint : null;
  const scenarioFill = resolvedScenarioColor
    ? (hexToRgba(resolvedScenarioColor, 0.16) || 'rgba(20, 20, 20, 0.8)')
    : (valenceTint || 'rgba(20, 20, 20, 0.8)');
  const scenarioFillHover = resolvedScenarioColor
    ? (hexToRgba(resolvedScenarioColor, 0.26) || 'rgba(30, 30, 30, 0.9)')
    : (valenceTint || 'rgba(30, 30, 30, 0.9)');

  // Create node group
  const nodeGroup = selection.append('g')
    .attr('class', 'tree-node')
    .style('cursor', 'pointer');

  if (isFreshHighlight) {
    const pad = 4;
    const glow = nodeGroup.append('rect')
      .attr('x', -cardWidth / 2 - pad)
      .attr('y', -cardHeight / 2 - pad)
      .attr('width', cardWidth + pad * 2)
      .attr('height', cardHeight + pad * 2)
      .attr('rx', 8)
      .attr('fill', 'rgba(91, 157, 217, 0.2)')
      .attr('stroke', '#5B9DD9')
      .attr('stroke-width', 2.5)
      .style('pointer-events', 'none');
    glow.append('animate')
      .attr('attributeName', 'opacity')
      .attr('from', 1)
      .attr('to', 0)
      .attr('dur', '2.2s')
      .attr('fill', 'freeze');
  }

  if (isPulsing) {
    const pulsePad = 3;
    const pulseRect = nodeGroup.append('rect')
      .attr('x', -cardWidth / 2 - pulsePad)
      .attr('y', -cardHeight / 2 - pulsePad)
      .attr('width', cardWidth + pulsePad * 2)
      .attr('height', cardHeight + pulsePad * 2)
      .attr('rx', 7)
      .attr('fill', 'none')
      .attr('stroke', '#5B9DD9')
      .attr('stroke-width', 2)
      .style('pointer-events', 'none');
    pulseRect.append('animate')
      .attr('attributeName', 'opacity')
      .attr('values', '0.2;1;0.2')
      .attr('dur', '1.15s')
      .attr('repeatCount', 'indefinite');
  }

  // If grouped, add stacked card effect (subtle shadows)
  if (isGrouped) {
    // Back shadow
    nodeGroup.append('rect')
      .attr('x', -cardWidth / 2 + 4)
      .attr('y', -cardHeight / 2 + 4)
      .attr('width', cardWidth)
      .attr('height', cardHeight)
      .attr('rx', 6)
      .attr('fill', 'rgba(60, 60, 60, 0.3)')
      .style('pointer-events', 'none');

    // Middle shadow
    nodeGroup.append('rect')
      .attr('x', -cardWidth / 2 + 2)
      .attr('y', -cardHeight / 2 + 2)
      .attr('width', cardWidth)
      .attr('height', cardHeight)
      .attr('rx', 6)
      .attr('fill', 'rgba(60, 60, 60, 0.4)')
      .style('pointer-events', 'none');
  }

  // Determine stroke color: grouped (gold), own node (bright cyan), scenario accent, default (gray)
  // With Valinnat on, the card border takes the valence colour so a wanted or
  // unwanted chain reads as one coloured route, not a row of rails. Faulty
  // input and grouping still outrank it — they are warnings, not readings.
  const valenceStroke = valence ? valenceStyle(valence).color : null;
  const getStrokeColor = () => {
    if (isFaultyInput) return FORM_CHECK_COLOR; // amber outline for faulty input
    if (isGrouped) return '#FFD700'; // Gold for grouped nodes
    if (valenceStroke) return valenceStroke;
    // Effect-type colour outranks "own node" cyan: what the edge means is more
    // informative than who typed it, and ownership still shows in the label.
    if (borderColor) return borderColor;
    if (resolvedScenarioColor) return resolvedScenarioColor;
    if (isOwnNode) return '#5B9DD9'; // Bright cyan for own nodes
    return '#444'; // Default gray
  };
  const strokeColor = getStrokeColor();
  const strokeWidth = isFaultyInput
    ? 2.5
    : (valenceStroke || borderColor || resolvedScenarioColor ? 2 : (isOwnNode ? 2 : 1));

  // Main card background — scenario roots use scenario colour border + tint
  const cardBg = nodeGroup.append('rect')
    .attr('x', -cardWidth / 2)
    .attr('y', -cardHeight / 2)
    .attr('width', cardWidth)
    .attr('height', cardHeight)
    .attr('rx', 6)
    .attr('fill', scenarioFill)
    .attr('stroke', strokeColor)
    .attr('stroke-width', strokeWidth);

  // ── Valence ──────────────────────────────────────────────────────────────
  // Tint and border carry the colour (above). Here only the shape cue.
  const showMark = Boolean(valence) && !isScenarioNode;
  if (showMark) {
    const vs = valenceStyle(valence);
    // Shape cue as well as colour — a third of a workshop cannot separate red
    // from green, and the report prints greyscale.
    nodeGroup.append('text')
      .attr('x', -cardWidth / 2 + 13)
      .attr('y', -cardHeight / 2 + 15)
      .attr('fill', vs.color)
      .attr('font-size', 12)
      .attr('font-weight', 700)
      .attr('pointer-events', 'none')
      .text(vs.mark);
  }

  // A fork into wanted and unwanted: the place the system can still go either
  // way. Derived, never authored — so it is marked, not labelled as a thing
  // somebody made.
  if (isChoicePoint) {
    const bx = cardWidth / 2 - 46;
    const by = -cardHeight / 2 - 9;
    nodeGroup.append('rect')
      .attr('x', bx)
      .attr('y', by)
      .attr('width', 46)
      .attr('height', 18)
      .attr('rx', 9)
      .attr('fill', 'rgba(0, 153, 255, 0.16)')
      .attr('stroke', '#0099ff')
      .attr('stroke-width', 1)
      .attr('pointer-events', 'none');
    nodeGroup.append('text')
      .attr('x', bx + 23)
      .attr('y', by + 13)
      .attr('text-anchor', 'middle')
      .attr('fill', '#66c2ff')
      .attr('font-size', 9)
      .attr('font-weight', 700)
      .attr('letter-spacing', '0.08em')
      .attr('pointer-events', 'none')
      .text('VALINTA');
  }

  // Add hover effect
  const getHoverStrokeColor = () => {
    if (isFaultyInput) return FORM_CHECK_COLOR;
    if (isGrouped) return '#FFD700'; // Gold stays gold
    if (valenceStroke) return valenceStroke;
    if (borderColor) return borderColor; // Typed edges keep their colour on hover
    if (resolvedScenarioColor) return resolvedScenarioColor;
    if (isOwnNode) return '#7BB8E8'; // Brighter cyan on hover
    return '#666'; // Default hover
  };
  cardBg.on('mouseenter', function() {
    d3.select(this)
      .attr('fill', scenarioFillHover)
      .attr('stroke', getHoverStrokeColor());
  }).on('mouseleave', function() {
    d3.select(this)
      .attr('fill', scenarioFill)
      .attr('stroke', strokeColor);
  });

  // Exclamation mark bubble for faulty input (formCheck warn state).
  if (isFaultyInput) {
    if (faultyTooltipText) {
      nodeGroup.append('title').text(faultyTooltipText);
    }

    const bubbleX = cardWidth / 2 - 18;
    const bubbleY = -cardHeight / 2 + 16;

    nodeGroup.append('circle')
      .attr('cx', bubbleX)
      .attr('cy', bubbleY)
      .attr('r', 10)
      .attr('fill', FORM_CHECK_COLOR)
      .attr('stroke', 'rgba(255,255,255,0.25)')
      .attr('stroke-width', 1)
      .style('pointer-events', 'none');

    nodeGroup.append('text')
      .attr('x', bubbleX)
      .attr('y', bubbleY + 4)
      .attr('text-anchor', 'middle')
      .attr('fill', FORM_CHECK_INK)
      .attr('font-size', '12px')
      .attr('font-weight', '900')
      .style('pointer-events', 'none')
      .text('!');
  }

  // === TOP LABEL ===
  // Format: "Skenaario · system" or "Muuttuja · system" or "Seuraus · TATU"
  // A muuttuja is anything flagged as a scenario starter below the root —
  // system-seeded ones carry type 'scenario_variable', user-created ones keep
  // their authored type ('human') but are still muuttujat. depth === 0 is
  // matched first below, so scenario roots keep the "Skenaario" label.
  const isVariableNode = type === 'scenario_variable' || is_scenario_starter === true;
  // A scenario node wears its scenario's icon, large, on the left; the label
  // and title move over to make room. The icon is the same Lucide glyph the
  // header picker shows, rendered once to markup — six nodes per map.
  const SCENARIO_ICON = 26;
  let inset = 0;
  if (isScenarioNode) {
    const Icon = getScenarioLucideIcon(title, data.icon);
    if (Icon) {
      nodeGroup.append('g')
        .attr('class', 'scenario-icon')
        .attr('transform', `translate(${-cardWidth / 2 + 14}, ${-SCENARIO_ICON / 2})`)
        .attr('pointer-events', 'none')
        .html(renderToStaticMarkup(createElement(Icon, {
          size: SCENARIO_ICON, strokeWidth: 1.75, color: resolvedScenarioColor || '#e5e7eb',
        })));
      inset = SCENARIO_ICON + 12;
    }
  }
  const labelX = -cardWidth / 2 + 16 + inset + (showMark ? 14 : 0);

  const typeLabel = isStartNode ? labels.start
    : depth === 0 ? labels.scenario
    : isVariableNode ? labels.variable
    : labels.consequence;
  const fullLabel = typeLabel;

  // Use green color for system nodes, gray for others
  const labelColor = created_by === 'system' ? '#4CAF50' : '#888';

  if (isStartNode && participantName) {
    nodeGroup.append('text')
      .attr('x', labelX)
      .attr('y', -cardHeight / 2 + 14)
      .attr('fill', '#4CAF50')
      .attr('font-size', '9px')
      .attr('font-weight', '600')
      .attr('text-transform', 'uppercase')
      .attr('letter-spacing', '0.5px')
      .text(participantName);
  } else {
    nodeGroup.append('text')
      .attr('x', labelX)
      .attr('y', -cardHeight / 2 + 14)
      .attr('fill', labelColor)
      .attr('font-size', '9px')
      .attr('font-weight', '600')
      .attr('text-transform', 'uppercase')
      .attr('letter-spacing', '0.5px')
      .text(fullLabel);
  }

  // === TITLE TEXT (Left-aligned, vertically centered) ===
  // Supports color-coded display: human text (blue) + AI analysis (white)
  const titleMaxWidth = cardWidth - 32 - inset;
  const titleX = -cardWidth / 2 + 16 + inset;
  const maxLines = 3;

  // Title text: prefer generated title (short headline), fallback to human_text
  const displayHumanText = title || human_text;

  // First pass: calculate lines to determine font size
  const words = displayHumanText.split(/\s+/);
  const estimateLines = (testFontSize) => {
    let line = '';
    let lineCount = 0;
    words.forEach((word) => {
      const testLine = line + (line ? ' ' : '') + word;
      const testWidth = testLine.length * (testFontSize * 0.55);
      if (testWidth > titleMaxWidth && line !== '') {
        lineCount++;
        line = word;
      } else {
        line = testLine;
      }
    });
    if (line !== '') lineCount++;
    return lineCount;
  };

  // Determine font size based on number of lines
  let fontSize;
  const estimatedLines = estimateLines(18);
  if (estimatedLines === 1) {
    fontSize = 20; // Bigger for single line
  } else if (estimatedLines === 2) {
    fontSize = 16; // Medium for two lines
  } else {
    fontSize = 14; // Smallest for three lines
  }

  const lineHeight = fontSize * 1.3;
  const titleGroup = nodeGroup.append('g');

  // Title always renders in white — human/AI color-coding is for descriptions only
  {
    const lines = [];
    let line = '';

    // Use only the title text (human_text or title), not AI analysis
    const titleWords = displayHumanText.split(/\s+/);
    titleWords.forEach((word) => {
      const testLine = line + (line ? ' ' : '') + word;
      const testWidth = testLine.length * (fontSize * 0.55);

      if (testWidth > titleMaxWidth && line !== '') {
        if (lines.length < maxLines) {
          lines.push(line);
        }
        line = word;
      } else {
        line = testLine;
      }
    });

    if (line !== '' && lines.length < maxLines) {
      lines.push(line);
    }

    // Calculate vertical positioning - upper area for expanded, centered for normal
    const totalTextHeight = lines.length * lineHeight;
    const startY = (isExpanded && hasDescription)
      ? -cardHeight / 2 + 30 + (lineHeight * 0.7)  // Top area for expanded
      : -(totalTextHeight / 2) + (lineHeight * 0.7);  // Centered for normal

    // Render lines — all white/light gray
    lines.forEach((lineText, i) => {
      titleGroup.append('text')
        .attr('x', titleX)
        .attr('y', startY + i * lineHeight)
        .attr('text-anchor', 'start')
        .attr('fill', '#fff')
        .attr('font-size', `${fontSize}px`)
        .attr('font-family', 'Rational, sans-serif')
        .attr('font-weight', '700')
        .text(lineText);
    });
  }

  // === DESCRIPTION (when expanded) - using foreignObject for proper text wrapping ===
  if (isExpanded && hasDescription) {
    // Divider line between title and description
    nodeGroup.append('line')
      .attr('x1', -cardWidth / 2 + 16)
      .attr('y1', -cardHeight / 2 + 75)
      .attr('x2', cardWidth / 2 - 16)
      .attr('y2', -cardHeight / 2 + 75)
      .attr('stroke', '#333')
      .attr('stroke-width', 1);

    // Use foreignObject for proper CSS text wrapping
    const descStartY = -cardHeight / 2 + 85; // Start right after title area
    const descHeight = cardHeight - 100; // Leave some padding
    const fo = nodeGroup.append('foreignObject')
      .attr('x', -cardWidth / 2 + 12)
      .attr('y', descStartY)
      .attr('width', cardWidth - 24)
      .attr('height', descHeight);

    // Build content with description + sources
    const hasAiSources = ai_sources && ai_sources.length > 0;
    const sourcesText = hasAiSources
      ? ai_sources.map(s => `📚 ${s.title || s}`).join('\n')
      : '';
    const fullContent = hasAiSources
      ? `${description}\n\n${sourcesText}`
      : description;

    fo.append('xhtml:div')
      .style('font-family', 'Rational, sans-serif')
      .style('font-size', '13px')
      .style('line-height', '1.5')
      .style('color', 'rgba(255, 255, 255, 0.85)')
      .style('overflow', 'hidden')
      .style('text-overflow', 'ellipsis')
      .style('display', '-webkit-box')
      .style('-webkit-line-clamp', '14')
      .style('-webkit-box-orient', 'vertical')
      .style('word-wrap', 'break-word')
      .style('white-space', 'pre-wrap')
      .style('padding', '0')
      .style('margin', '0')
      .text(fullContent);
  }

  // === GROUPED NODE INDICATOR ===
  if (isGrouped) {
    nodeGroup.append('text')
      .attr('x', -cardWidth / 2 + 16)
      .attr('y', cardHeight / 2 - 12)
      .attr('text-anchor', 'start')
      .attr('fill', '#FFD700')
      .attr('font-size', '10px')
      .attr('font-weight', '600')
      .text(`${members.length + 1} merged`);
  }

  // === AI SCORES (not for muuttujat) ===
  const scores = is_scenario_starter ? null : (node_analysis?.scores || node_analysis);
  if (scores && (scores.research_support != null || scores.quality != null) && !isExpanded) {
    const rs = scores.research_support;
    const q = scores.quality;
    const rsColor = rs >= 0 ? '#4ade80' : '#f87171';
    const qColor = q >= 25 ? '#4ade80' : '#f87171';
    const scoreY = cardHeight / 2 - 14;

    const iconSize = 12;
    const iconY = scoreY - iconSize + 3;

    // BookOpen icon (Tutkimustuki) — Lucide path scaled to 12px
    const bookG = nodeGroup.append('g')
      .attr('transform', `translate(${-cardWidth / 2 + 14}, ${iconY}) scale(0.5)`);
    bookG.append('path')
      .attr('d', 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z')
      .attr('fill', 'none').attr('stroke', rsColor).attr('stroke-width', 2).attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round');
    bookG.append('path')
      .attr('d', 'M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z')
      .attr('fill', 'none').attr('stroke', rsColor).attr('stroke-width', 2).attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round');

    nodeGroup.append('text')
      .attr('x', -cardWidth / 2 + 30)
      .attr('y', scoreY)
      .attr('fill', rsColor)
      .attr('font-size', '10px')
      .attr('font-weight', '600')
      .text(rs);

    // Target icon (Osuvuus) — Lucide path scaled to 12px
    const targetG = nodeGroup.append('g')
      .attr('transform', `translate(${-cardWidth / 2 + 58}, ${iconY}) scale(0.5)`);
    targetG.append('circle')
      .attr('cx', 12).attr('cy', 12).attr('r', 10)
      .attr('fill', 'none').attr('stroke', qColor).attr('stroke-width', 2);
    targetG.append('circle')
      .attr('cx', 12).attr('cy', 12).attr('r', 6)
      .attr('fill', 'none').attr('stroke', qColor).attr('stroke-width', 2);
    targetG.append('circle')
      .attr('cx', 12).attr('cy', 12).attr('r', 2)
      .attr('fill', qColor);

    nodeGroup.append('text')
      .attr('x', -cardWidth / 2 + 74)
      .attr('y', scoreY)
      .attr('fill', qColor)
      .attr('font-size', '10px')
      .attr('font-weight', '600')
      .text(q);
  }

  // === UNREAD AI DOT (evaluation ready, not yet opened) ===
  if (hasUnreadAi && !isProcessing) {
    appendAiUnreadDot(nodeGroup, {
      x: -cardWidth / 2 - 2,
      y: -cardHeight / 2 - 2,
      radius: 5,
      scale: zoomDotScale,
    });
  }

  // === PENDING AI PROPOSAL (a loop or cross-link waiting on a decision) ===
  // Top-right, opposite the unread-analysis dot, because they are different
  // errands: one is "your node was examined", this is "the map may connect
  // somewhere you have not looked".
  if (hasSuggestion) {
    const mark = nodeGroup.append('g')
      .attr('class', 'node-suggestion-mark')
      .attr('transform', `translate(${cardWidth / 2 - 13}, ${-cardHeight / 2 + 13})`);
    mark.append('circle')
      .attr('r', 8)
      .attr('fill', '#14141f')
      .attr('stroke', '#AB7DF6')
      .attr('stroke-width', 1.6);
    mark.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', 10)
      .attr('font-weight', 800)
      .attr('fill', '#AB7DF6')
      .style('user-select', 'none')
      .text('?');
    mark.append('title').text('Tekoäly ehdottaa yhteyttä tästä solmusta — tarkistamatta');
  }

  // === PROCESSING GLIMMER (AI working on this node) ===
  if (isProcessing) {
    const glimmerY = cardHeight / 2 - 2;
    const glimmerWidth = cardWidth - 8;

    // Background track
    nodeGroup.append('rect')
      .attr('x', -glimmerWidth / 2)
      .attr('y', glimmerY)
      .attr('width', glimmerWidth)
      .attr('height', 2)
      .attr('rx', 1)
      .attr('fill', 'rgba(147, 51, 234, 0.15)');

    // Animated shimmer using SVG animate — skip on low-gfx TVs where
    // indefinite SMIL animations contribute to GPU compositor corruption.
    // Static highlight bar still signals "AI working".
    const shimmer = nodeGroup.append('rect')
      .attr('x', -glimmerWidth / 2)
      .attr('y', glimmerY)
      .attr('width', glimmerWidth * 0.4)
      .attr('height', 2)
      .attr('rx', 1)
      .attr('fill', 'rgba(147, 51, 234, 0.6)');

    if (!isLowGfx()) {
      shimmer.append('animate')
        .attr('attributeName', 'x')
        .attr('values', `${-glimmerWidth / 2};${glimmerWidth / 2 - glimmerWidth * 0.4}`)
        .attr('dur', '1.5s')
        .attr('repeatCount', 'indefinite');
    }
  }

  // === REACTIONS INDICATOR (if non-zero) ===
  if (reactions !== 0) {
    const reactionColor = reactions > 0 ? '#4CAF50' : '#EF5350';
    const reactionText = reactions > 0 ? `+${reactions}` : `${reactions}`;

    nodeGroup.append('circle')
      .attr('cx', cardWidth / 2 - 20)
      .attr('cy', -cardHeight / 2 + 20)
      .attr('r', 12)
      .attr('fill', reactionColor)
      .attr('opacity', 0.9);

    nodeGroup.append('text')
      .attr('x', cardWidth / 2 - 20)
      .attr('y', -cardHeight / 2 + 24)
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .attr('font-size', '11px')
      .attr('font-weight', '700')
      .text(reactionText);
  }

  return nodeGroup.node();
}

// Export default for backward compatibility
export default renderImpactPathNode;
